import { PLAN_NAMES, SUPPLIER_PLANS } from "constants/data";

export type GridRatio = {
  image: number;
  content: number;
};

export type PlanConfig = {
  showImages: boolean;
  gridRatio: GridRatio;
  layoutVersion: "premium" | "basic";
  canDisplayMedia: boolean;
};

/**
 * Returns whether a plan should show real media (images/gallery) if available
 * @param planName The plan name to check
 * @param hasMedia Whether media exists to display
 */
export const shouldShowRealMedia = (
  planName: string | undefined,
  hasMedia: boolean
): boolean => {
  if (!hasMedia) return false;
  
  // All plans can show images if they have them
  // Premium plans (Advance and Starter) will show more images based on their configuration
  return !!planName;
};

/**
 * Returns the grid ratio configuration for a given plan
 * @param planName The plan name to check
 * @param isMobile Whether the current view is on mobile
 * @param hasMedia Whether media exists to display
 */
export const getGridRatio = (
  planName: string | undefined,
  isMobile: boolean,
  hasMedia: boolean = false
): GridRatio => {
  if (isMobile) {
    // On mobile, use full width for both image and content
    return { image: 12, content: 12 };
  }

  // Plan-specific grid ratios
  switch (planName) {
    case PLAN_NAMES.starter:
      return { image: 3, content: 9 }; // 1:3 ratio (3:9)
    case PLAN_NAMES.advance:
      return { image: 4, content: 8 }; // 1:2 ratio (4:8)
    default:
      // For all other plans, show one image if available, otherwise hide images
      return hasMedia ? { image: 3, content: 9 } : { image: 0, content: 12 };
  }
};

/**
 * Checks if the plan should use the premium layout (with images)
 * @param planName The plan name to check
 * @param isMobile Whether the current view is on mobile
 * @param hasMedia Whether media exists to display
 */
export const hasPremiumLayout = (
  planName: string | undefined, 
  isMobile: boolean,
  hasMedia: boolean = false
): boolean => {
  const premiumPlans = [PLAN_NAMES.advance, PLAN_NAMES.starter, PLAN_NAMES.pro];
  const isPremiumPlan = !!planName && premiumPlans.includes(planName);
  const isNonPremiumPlan = !!planName && !premiumPlans.includes(planName);
  
  // Desktop layout rules:
  // - Premium plans always get premium layout
  // - Any plan with media gets premium layout on desktop
  if (!isMobile) {
  return isPremiumPlan || hasMedia;
  }
  
  // Mobile layout rules:
  // - Premium plans get premium layout if they have media
  // - Non-premium plans with media get basic layout
  return isPremiumPlan && hasMedia;
};

/**
 * Gets the gradient for the plan border
 * @param planName The plan name
 */
export const getPlanGradient = (planName: string | undefined): string | undefined => {
  if (!planName) return undefined;
  
  return SUPPLIER_PLANS.find((plan) => plan.label === planName)?.linear;
};

/**
 * Gets complete plan configuration
 * @param planName The plan name
 * @param isMobile Whether the current view is on mobile
 * @param hasMedia Whether media exists to display
 */
export const getPlanConfig = (
  planName: string | undefined,
  isMobile: boolean,
  hasMedia: boolean
): PlanConfig => {
  const gridRatio = getGridRatio(planName, isMobile, hasMedia);
  const isPremium = hasPremiumLayout(planName, isMobile, hasMedia);
  const canDisplayMedia = shouldShowRealMedia(planName, hasMedia);
  
  return {
    showImages: canDisplayMedia && gridRatio.image > 0,
    gridRatio,
    layoutVersion: isPremium ? "premium" : "basic",
    canDisplayMedia,
  };
}; 